import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import Sidebar from "@/components/Sidebar";
import { api } from "@/lib/api";
import { Activity, Search } from "lucide-react";

const ACTION_LABELS = {
  login: "Logged in",
  logout: "Logged out",
  quotation_created: "Created quotation",
  quotation_updated: "Edited quotation",
  quotation_pdf_downloaded: "Downloaded quotation PDF",
  voucher_created: "Created hotel voucher",
  voucher_pdf_downloaded: "Downloaded voucher PDF",
  invoice_created: "Created invoice",
  itinerary_generated: "Generated AI itinerary",
};
const ACTION_OPTIONS = Object.entries(ACTION_LABELS);

function fmtTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

function StatCard({ label, value, color = "emerald", testid }) {
  const map = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
    slate: "border-slate-200 bg-slate-50 text-slate-700",
    indigo: "border-indigo-200 bg-indigo-50 text-indigo-700",
    amber: "border-amber-200 bg-amber-50 text-amber-700",
  }[color];
  return (
    <div className={`border rounded p-4 ${map}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest opacity-80">{label}</div>
      <div className="font-editorial text-3xl mt-1 text-slate-900">{value ?? 0}</div>
    </div>
  );
}

export default function AdminActivity() {
  const [summary, setSummary] = useState({});
  const [online, setOnline] = useState([]);
  const [rows, setRows] = useState([]);
  const [agents, setAgents] = useState([]);
  const [filters, setFilters] = useState({ agent_id: "", action: "", date_from: "", date_to: "", q: "" });
  const [paused, setPaused] = useState(false);
  const polling = useRef(null);

  const load = async () => {
    try {
      const [s, o, a] = await Promise.all([
        api.get("/admin/activity/summary"),
        api.get("/admin/activity/online"),
        api.get("/admin/activity", { params: filters }),
      ]);
      setSummary(s.data || {}); setOnline(o.data || []); setRows(a.data || []);
    } catch (e) {
      // Silently retry — avoid toast storm on long sessions
    }
  };

  useEffect(() => {
    api.get("/admin/activity/agents").then(({ data }) => setAgents(data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    if (paused) return;
    polling.current = setInterval(load, 5000);
    return () => clearInterval(polling.current);
    // eslint-disable-next-line
  }, [filters, paused]);

  const clearFilters = () => setFilters({ agent_id: "", action: "", date_from: "", date_to: "", q: "" });

  return (
    <div className="flex min-h-screen">
      <Sidebar role="admin" />
      <main className="flex-1 bg-[#FCFCFC] p-6 lg:p-10">
        <div className="max-w-[1400px]">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="overline text-emerald-700">Admin · Live Reports</div>
              <h1 className="font-editorial text-3xl mt-1 text-slate-900 flex items-center gap-3">
                <Activity size={28} className="text-emerald-600" /> Agent Activity
              </h1>
            </div>
            <label className="inline-flex items-center gap-2 text-xs cursor-pointer" data-testid="activity-pause-toggle">
              <input type="checkbox" checked={paused} onChange={(e) => setPaused(e.target.checked)} />
              <span className="uppercase tracking-widest text-slate-500">{paused ? "Live paused" : "Live"}</span>
              <span className={`w-2 h-2 rounded-full ${paused ? "bg-slate-400" : "bg-emerald-500 animate-pulse"}`} />
            </label>
          </div>

          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
            <StatCard label="Active Agents" value={summary.active_agents} testid="stat-active" />
            <StatCard label="Activities Today" value={summary.total_today} color="slate" testid="stat-total" />
            <StatCard label="Quotations Created" value={summary.quotations_created} color="indigo" testid="stat-qc" />
            <StatCard label="Vouchers Created" value={summary.vouchers_created} color="indigo" testid="stat-vc" />
            <StatCard label="Invoices Created" value={summary.invoices_created} color="indigo" testid="stat-ic" />
            <StatCard label="Quotation PDFs" value={summary.quotations_downloaded} color="amber" testid="stat-qpdf" />
            <StatCard label="Voucher PDFs" value={summary.vouchers_downloaded} color="amber" testid="stat-vpdf" />
            <StatCard label="AI Itineraries" value={summary.itineraries_generated} color="emerald" testid="stat-itin" />
            <StatCard label="Logins Today" value={summary.logins_today} color="slate" testid="stat-login" />
            <StatCard label="Online Right Now" value={online.filter(o => o.online).length} color="emerald" testid="stat-online" />
          </div>

          {/* Online agents strip */}
          <div className="bg-white border border-border rounded p-4 mb-6">
            <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">Live sessions</div>
            {online.length === 0 && <div className="text-sm text-slate-400">No sessions recorded yet.</div>}
            <div className="flex flex-wrap gap-2">
              {online.map((o) => (
                <div key={o.user_id} data-testid={`online-${o.user_id}`} className={`border rounded px-3 py-2 text-xs flex items-center gap-2 ${o.online ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-slate-50"}`}>
                  <span className={`w-2 h-2 rounded-full ${o.online ? "bg-emerald-500" : "bg-slate-400"}`} />
                  <div>
                    <div className="font-semibold text-slate-900">{o.agency_name || o.agent_email}</div>
                    <div className="text-[10px] text-slate-500">
                      {o.online ? "Online" : "Offline"} · last {fmtTime(o.last_active)}
                      {o.current_page ? ` · ${o.current_page}` : ""}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white border border-border rounded p-4 mb-4 grid grid-cols-1 md:grid-cols-6 gap-3">
            <div className="md:col-span-2 relative">
              <Search size={13} className="absolute left-3 top-3 text-slate-400" />
              <input data-testid="activity-search" placeholder="Search agent / agency / entity"
                value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                className="w-full border border-border rounded pl-8 pr-3 py-2 text-sm" />
            </div>
            <select data-testid="activity-agent-filter" value={filters.agent_id}
              onChange={(e) => setFilters({ ...filters, agent_id: e.target.value })}
              className="border border-border rounded px-2 py-2 text-sm bg-white">
              <option value="">All agents</option>
              {agents.map((a) => <option key={a.id} value={a.id}>{a.agency_name || a.email}</option>)}
            </select>
            <select data-testid="activity-action-filter" value={filters.action}
              onChange={(e) => setFilters({ ...filters, action: e.target.value })}
              className="border border-border rounded px-2 py-2 text-sm bg-white">
              <option value="">All actions</option>
              {ACTION_OPTIONS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
            </select>
            <input data-testid="activity-from" type="date" value={filters.date_from}
              onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
              className="border border-border rounded px-2 py-2 text-sm" />
            <input data-testid="activity-to" type="date" value={filters.date_to}
              onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
              className="border border-border rounded px-2 py-2 text-sm" />
            <button data-testid="activity-clear" onClick={clearFilters}
              className="md:col-span-6 text-xs uppercase tracking-widest text-slate-500 hover:text-emerald-700 self-start">
              Clear filters
            </button>
          </div>

          {/* Activity log */}
          <div className="bg-white border border-border rounded overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-widest">
                <tr>
                  <th className="text-left px-4 py-2.5">Time</th>
                  <th className="text-left px-4 py-2.5">Agent / Agency</th>
                  <th className="text-left px-4 py-2.5">Activity</th>
                  <th className="text-left px-4 py-2.5">Reference</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={4} className="px-4 py-6 text-center text-slate-400">No activity matching current filters.</td></tr>
                )}
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`activity-row-${r.id}`} className="border-t border-border hover:bg-slate-50">
                    <td className="px-4 py-2 text-slate-600 whitespace-nowrap">{fmtTime(r.timestamp)}</td>
                    <td className="px-4 py-2">
                      <div className="font-semibold text-slate-900">{r.agency_name || r.agent_email}</div>
                      {r.agency_name && <div className="text-[11px] text-slate-500">{r.agent_email}</div>}
                    </td>
                    <td className="px-4 py-2 text-slate-700">{ACTION_LABELS[r.action] || r.action}</td>
                    <td className="px-4 py-2 text-slate-500 font-mono-e text-xs">{r.entity_number || r.entity_id || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}
